// src/AppInitializer.tsx
import React, { useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import { SnackbarProvider, SnackbarKey } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFnsJalali } from "@mui/x-date-pickers/AdapterDateFnsJalaliV3";
import {
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Typography,
} from "@mui/material";

import App from "./App";
import { configs } from "./constants/Constants";
import { snackActions, SnackbarUtilsConfigurator } from "./utils/SnackbarUtils";
import RTL from "./components/Layout/RTL/RTL";
import { reinitializeService } from "./utils/Request";
import { initializeQueryClient, queryClient } from "./utils/QueryClient";
import { configService } from "./utils/ConfigService";
import { betaTheme } from "@BehpardazBarin/frontend_components";

const LoadingScreen = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    flexDirection="column"
  >
    <CircularProgress />
    <Typography variant="h6" sx={{ mt: 2 }}>
      درحال گرفتن تنظیمات سامانه
    </Typography>
  </Box>
);

const ErrorScreen = ({ message }: { message: string }) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    flexDirection="column"
    p={3}
  >
    <Typography variant="h6" color="error" gutterBottom>
      خطای تنظیمات
    </Typography>
    <Typography variant="body1" align="center">
      {message}
    </Typography>
    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
      عدم بارگزاری تنظیمات. لطفا با ادمین سایت تماس بگیرید.
    </Typography>
  </Box>
);

const dismissAction = (key: SnackbarKey) => (
  <Button
    onClick={() => snackActions.closeSnackbar(key)}
    style={{
      height: "100%",
      left: 0,
      position: "absolute",
      top: 0,
      width: "100%",
    }}
  />
);

export const AppInitializer: React.FC = () => {
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const initialize = async () => {
      try {
        console.log("AppInitializer: Starting configuration load...");
        await configService.loadConfig();

        // Initialize API Service (Axios, Request , ...)
        reinitializeService();

        // Initialize the QueryClient
        initializeQueryClient();

        console.log(
          "AppInitializer: Configuration and services initialized successfully."
        );
        setStatus("success");
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "An unknown error occurred during initialization.";
        console.error("AppInitializer: Failed to initialize app:", error);
        setErrorMessage(message);
        setStatus("error");
      }
    };

    initialize();
  }, []); // this runs only once on mount

  if (status === "loading") {
    return <LoadingScreen />;
  }

  if (status === "error") {
    return <ErrorScreen message={errorMessage} />;
  }

  return (
    <BrowserRouter>
      <RTL>
        <ThemeProvider theme={betaTheme}>
          <CssBaseline />
          <SnackbarProvider
            maxSnack={configs.MAX_SNAKBAR}
            autoHideDuration={configs.HIDE_DURATION}
            action={dismissAction}
          >
            <SnackbarUtilsConfigurator />
            <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
              <QueryClientProvider client={queryClient}>
                <App />
              </QueryClientProvider>
            </LocalizationProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </RTL>
    </BrowserRouter>
  );
};
